import React, { createContext, useCallback, useContext, useState } from "react";
import { ErrorNotification, NotificationSeverity } from "../components/ErrorNotification/ErrorNotification";

interface ErrorNotificationContextValue {
    showError: (message: string, title?: string, severity?: NotificationSeverity) => void;
}

const ErrorNotificationContext = createContext<ErrorNotificationContextValue | undefined>(undefined);

export const ErrorNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [error, setError] = useState<{ message: string; title: string; severity: NotificationSeverity } | null>(null);

    const showError = useCallback(
        (message: string, title = "Something went wrong", severity: NotificationSeverity = "error") => {
            setError({ message, title, severity });
        },
        []
    );

    const dismiss = useCallback(() => {
        setError(null);
    }, []);

    return (
        <ErrorNotificationContext.Provider value={{ showError }}>
            {children}
            {error && (
                <ErrorNotification
                    title={error.title}
                    message={error.message}
                    severity={error.severity}
                    onClose={dismiss}
                />
            )}
        </ErrorNotificationContext.Provider>
    );
};

export const useErrorNotification = (): ErrorNotificationContextValue => {
    const ctx = useContext(ErrorNotificationContext);
    if (!ctx) throw new Error("useErrorNotification must be used within ErrorNotificationProvider");
    return ctx;
};
